import { useEffect, useMemo, useState, type JSX } from "react";
import { useThree } from "@react-three/fiber";
import { attachPass } from "@faro-lotv/app-component-toolbox";
import type { LodPointCloud } from "@faro-lotv/lotv";
import { Matrix4, Vector3 } from "three";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { setClashDisplayError } from "@/store/point-cloud-analysis-tool-slice";
import { selectActiveAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import { isUnknownObject } from "@/object-cache-type-guard";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { SurfacePointFilter } from "../surface-wand/surface-point-filter";

/** @returns Display-only opacity outside processed cells of the active analysis. */
export function CadClashCoverageEffect({
  pointCloud,
}: {
  pointCloud: LodPointCloud | null;
}): JSX.Element | null {
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);
  const events = useSceneEvents();
  const dispatch = useAppDispatch();
  const [maskReady, setMaskReady] = useState(false);
  const analysis = useAppSelector(selectActiveAnalysis);
  const opacity = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashContextOpacity ?? 1,
  );
  const status = useAppSelector((state) =>
    analysis
      ? state.pointCloudAnalysisTool.clashStatus?.[analysis.id]
      : undefined,
  );
  const processing =
    analysis?.kind === "cad" ? analysis.clashes?.processing : undefined;
  const enabled =
    !!processing?.usedCells.length &&
    opacity < 1 &&
    analysis?.isVisible === true &&
    isUnknownObject(pointCloud) &&
    pointCloud.iElement.id === analysis.parentId &&
    status?.analysisId === analysis.id &&
    status.phase === "ready";
  const filter = useMemo(() => new SurfacePointFilter(camera), [camera]);
  useEffect(() => () => filter.dispose(), [filter]);
  useEffect(() => {
    setMaskReady(false);
    if (!enabled) return;
    try {
      filter.setCells(
        processing.usedCells,
        new Matrix4().fromArray(processing.frame ?? new Matrix4().toArray()),
        new Vector3().fromArray(processing.usedCells[0][0]),
      );
      setMaskReady(true);
      dispatch(setClashDisplayError(undefined));
    } catch {
      dispatch(
        setClashDisplayError(
          "Cell opacity is unavailable for this coverage size; all points remain visible",
        ),
      );
    }
  }, [filter, processing, enabled, dispatch]);
  useEffect(() => {
    filter.setOpacity(opacity);
    filter.setEnabled(enabled && maskReady);
    events.invalidatePointCloudScene.emit();
    invalidate();
  }, [enabled, maskReady, opacity, filter, events, invalidate]);
  useEffect(() => {
    if (!enabled || !maskReady) return;
    const restore = filter.attach(pointCloud.material);
    return () => {
      restore();
      events.invalidatePointCloudScene.emit();
      invalidate();
    };
  }, [filter, enabled, maskReady, pointCloud, events, invalidate]);
  return <primitive object={filter} attach={attachPass} />;
}
